/**
 * The inboxes the report scan reads, one row per address however it was
 * connected: a Google account connected with the Gmail permission (read
 * through Google's own API) and/or an app password (read over IMAP).
 * Configuration → Email & Sheets showed the two as separate tables -
 * "Gmail inboxes (app password)" listed only the app-password ones - so a
 * mailbox connected through its Google sign-in looked unscanned, and an app
 * password nobody remembered adding looked like the only inbox (2026-09-15).
 * The scan itself reads both lists (server email/scan.ts); an address
 * connected both ways is read twice, which its row says.
 */
export interface GoogleInbox {
  email: string;
  user: string | null;
  /** consented to the Gmail permission - a Sheets-only account is not an inbox */
  gmail: boolean;
  lastImport: string | null;
}

export interface PasswordInbox {
  id: number;
  email: string;
  enabled: boolean;
  user: string | null;
  lastImport: string | null;
}

export type InboxVia = 'google' | 'password';

export interface InboxRow {
  email: string;
  via: InboxVia[];
  /** every user the address is filed under, deduplicated, in the order connected */
  users: string[];
  lastImport: string | null;
  /** the app-password entry when there is one - what Remove and the user picker act on */
  password: PasswordInbox | null;
}

export function inboxRows(google: readonly GoogleInbox[], passwords: readonly PasswordInbox[]): InboxRow[] {
  const rows = new Map<string, InboxRow>();
  const key = (e: string) => e.trim().toLowerCase();
  for (const g of google) {
    if (!g.gmail) continue;
    rows.set(key(g.email), { email: g.email.trim(), via: ['google'], users: g.user ? [g.user] : [], lastImport: g.lastImport, password: null });
  }
  for (const p of passwords) {
    const cur = rows.get(key(p.email));
    if (cur) {
      cur.via.push('password');
      if (p.user && !cur.users.includes(p.user)) cur.users.push(p.user);
      cur.lastImport = later(cur.lastImport, p.lastImport);
      cur.password = p;
    } else {
      rows.set(key(p.email), { email: p.email.trim(), via: ['password'], users: p.user ? [p.user] : [], lastImport: p.lastImport, password: p });
    }
  }
  return [...rows.values()];
}

/** ISO-ish timestamps compare as text. */
function later(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

export function viaLabel(via: readonly InboxVia[]): string {
  return via.map((v) => (v === 'google' ? 'Google sign-in' : 'app password')).join(' + ');
}
